import { useCallback, useEffect, useState } from 'react'

import PublicAppBar from '@/components/PublicAppBar'
import PublicFooter from '@/components/PublicFooter'
import { CONTAINER_MAX_WIDTH } from '@/config/constants'
import useFeatureFlags from '@/hooks/useFeatureFlags'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Button, Field, Flex, HStack, Heading, Icon, Input, Spacer, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'
import { validateEmail } from '@infraloop/services'
import mixpanel from 'mixpanel-browser'
import { LuCircleCheck } from 'react-icons/lu'
import { Navigate, useSearchParams } from 'react-router'

import useRegisterUser from './hooks/useRegisterUser'

/**
 * Public sign-up form for a free viewing account. Reached from the landing page and from
 * the invitation mails, which carry the invited address in `?email_address=`.
 */
const Register = () => {
  // Hooks
  const { t } = useTranslation()
  const { isAuthenticated, loginWithRedirect } = useAuth0()
  const [searchParams] = useSearchParams()
  const { featureFlags, isLoadingFeatureFlags } = useFeatureFlags()
  const { registerUser, isRegisteringUser } = useRegisterUser()

  // States
  const [emailAddress, setEmailAddress] = useState('')
  const [name, setName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [isCreated, setIsCreated] = useState(false)

  // Derived state — self-managed subscriptions additionally collect a name and company,
  // which the backend attaches to the new account.
  const emailFromLink = searchParams.get('email_address') ?? ''
  const collectsProfile = Boolean(featureFlags?.selfManagedSubscriptions.enabled)

  // Submitting is held back until the flags land: the name and company fields only appear
  // once they do, so an early click could otherwise create an account without them.
  const isSubmitDisabled =
    isRegisteringUser ||
    isLoadingFeatureFlags ||
    !validateEmail(emailAddress) ||
    (collectsProfile && (!name || !companyName))

  /**
   * Create the account, then swap the form for the check-your-inbox confirmation. A failed
   * attempt is reported by the mutation itself and leaves the form as it was.
   */
  const onCreateUser = useCallback(async () => {
    try {
      await registerUser({ emailAddress, name, companyName })
      setIsCreated(true)
      mixpanel.track('Create user account', { email: emailAddress })
    } catch {
      // Already surfaced as a toast by the mutation's error handler.
    }
  }, [registerUser, emailAddress, name, companyName])

  /**
   * Seed the address from the invitation link, and reset a previous confirmation when a
   * second link is opened in the same tab.
   */
  useEffect(() => {
    setEmailAddress(emailFromLink)
    setIsCreated(false)
  }, [emailFromLink])

  // Someone already signed in has no account to create; send them to their dashboard.
  if (isAuthenticated) return <Navigate to="/" replace />

  return (
    <Flex direction="column" minH="100vh" bg="gray.50">
      <PublicAppBar />

      <Flex direction="column" flex="1" w="100%" maxW={CONTAINER_MAX_WIDTH} mx="auto" px="28px" py="34px">
        <Heading as="h1" size="xl" py="7">
          {t('hatsuly.register.title')}
        </Heading>

        {!isCreated && (
          <>
            <Box mb={4}>
              <Text whiteSpace="pre-line">{t('hatsuly.register.description')}</Text>
            </Box>
            <VStack w={{ base: '100%', md: '50%' }} align="flex-start" gap={4}>
              <Field.Root>
                <Field.Label>{t('hatsuly.register.email')}</Field.Label>
                <Input
                  id="email_address"
                  type="text"
                  bg="white"
                  value={emailAddress}
                  disabled={isRegisteringUser}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  data-testid="register-email"
                />
              </Field.Root>
              {collectsProfile && (
                <>
                  <Field.Root>
                    <Field.Label>{t('hatsuly.register.name')}</Field.Label>
                    <Input
                      id="name"
                      type="text"
                      bg="white"
                      value={name}
                      disabled={isRegisteringUser}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="register-name"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>{t('hatsuly.register.company')}</Field.Label>
                    <Input
                      id="company_name"
                      type="text"
                      bg="white"
                      value={companyName}
                      disabled={isRegisteringUser}
                      onChange={(e) => setCompanyName(e.target.value)}
                      data-testid="register-company"
                    />
                  </Field.Root>
                </>
              )}
              <Button
                colorPalette="blue"
                minW={20}
                disabled={isSubmitDisabled}
                onClick={() => {
                  void onCreateUser()
                }}
                data-testid="register-submit"
              >
                {t('hatsuly.register.submit')}
              </Button>
            </VStack>
          </>
        )}

        {isCreated && (
          <VStack align="flex-start" gap={4} data-testid="register-success">
            <HStack color="primary.500">
              <Icon as={LuCircleCheck} boxSize="24px" />
              <Text fontSize="md" fontWeight="bold">
                {t('hatsuly.register.successTitle')}
              </Text>
            </HStack>
            <Text>
              {t('hatsuly.register.successBody')}
              <Text as="span" display="block" color="gray.400">
                ({emailAddress})
              </Text>
            </Text>
            <Button
              colorPalette="blue"
              onClick={() => {
                void loginWithRedirect()
              }}
              data-testid="register-login"
            >
              {t('hatsuly.nav.login')}
            </Button>
          </VStack>
        )}

        <Spacer />
        <PublicFooter />
      </Flex>
    </Flex>
  )
}

export default Register
